// Bay & Tides, two pages since Oct 26 (Jared: one page was too busy): 1) the tides, the next highs and lows at Fort
// Point beside today's curve; 2) the ocean (bar buoy swell, water, wind) and the Golden Gate current beside the bay
// forecast or advisory; 3) since Oct 27 (Phase E, a merge Jared approved), the Beach Day score (renamed that evening from Beach & Water, so it reads apart from the crab score) for Ocean Beach and
// Baker Beach right now (config/water.json `beach`)
import tierOf, {
	beachAlert, inEffect, dangers, alertWhen,
} from './advisories.mjs';
import { registerDisplay } from '../navigation.mjs';
import { escapeHtml } from '../utils/string.mjs';
import FeatureDisplay from './feature-display.mjs';
import { data } from './snapshot.mjs';
import {
	time, weekday, isToday, asOf, compass, feet, dayKey,
} from './format.mjs';

const { water } = window.RHL;
const HOUR = 3600000;
const byRole = (buoys, role) => buoys?.find((b) => b.role === role) ?? null;

// today's half-hourly tide curve as an SVG line, with highs and lows marked and a line at now
const tideChart = (tides, now) => {
	const start = new Date(tides.curve.start).getTime();
	const step = tides.curve.stepMinutes * 60000;
	const today = dayKey(now);
	const points = tides.curve.ft
		.map((ft, i) => ({ t: start + i * step, ft }))
		.filter((p) => dayKey(p.t) === today);
	if (points.length < 2) return '';
	const width = 330;
	const height = 120;
	const t0 = points[0].t;
	const t1 = points.at(-1).t;
	const lo = Math.min(...points.map((p) => p.ft));
	const hi = Math.max(...points.map((p) => p.ft));
	const x = (t) => ((t - t0) / (t1 - t0)) * width;
	const y = (ft) => 6 + (1 - (ft - lo) / (hi - lo || 1)) * (height - 12);
	const path = points.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(1)} ${y(p.ft).toFixed(1)}`).join(' ');
	const marks = tides.events
		.filter((e) => dayKey(e.t) === today)
		.map((e) => `<circle cx="${x(new Date(e.t).getTime()).toFixed(1)}" cy="${y(e.ft).toFixed(1)}" r="4" fill="#ffff00"/>`)
		.join('');
	const nowX = x(now.getTime()).toFixed(1);
	return `<svg class="tide-chart" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" aria-hidden="true">
		<path d="${path}" fill="none" stroke="#8ffdfa" stroke-width="3"/>${marks}
		<line x1="${nowX}" y1="0" x2="${nowX}" y2="${height}" stroke="#ffff00" stroke-width="2" stroke-dasharray="4 3"/>
	</svg>
	<div class="chart-axis"><span>12a</span><span>6a</span><span>noon</span><span>6p</span><span>12a</span></div>`;
};

// rising or falling from the curve around now
const tideTrend = (tides, now) => {
	const start = new Date(tides.curve.start).getTime();
	const i = Math.floor((now - start) / (tides.curve.stepMinutes * 60000));
	const [a, b] = [tides.curve.ft[i], tides.curve.ft[i + 1]];
	if (a === undefined || b === undefined) return '';
	return b > a ? 'rising' : 'falling';
};

const kv = (rows) => `<div class="kv">${rows.filter(Boolean).map(([k, v]) => `<div>${k}</div><div class="v">${v}</div>`).join('')}</div>`;

// the hourly value nearest now in an Open-Meteo series, within two hours
const nearest = (times, values, now) => {
	if (!times?.length || !values) return null;
	const i = times.reduce((best, t, idx) => (Math.abs(new Date(t) - now) < Math.abs(new Date(times[best]) - now) ? idx : best), 0);
	return Math.abs(new Date(times[i]) - now) <= 2 * HOUR ? values[i] : null;
};
const scale = (value, full, zero) => Math.max(0, Math.min(1, (value - zero) / (full - zero)));

// the Beach Day score: surf, wind, low cloud and warmth scaled 0-1 and weighted; a part without data drops out
// and the others carry its weight; a rip current, beach hazards or high surf statement caps it while it's in effect
// (Nov 1: not from the moment it's issued, a day or more ahead)
const beachScore = (now) => {
	const cfg = water.beach;
	if (!cfg) return null;
	const waves = byRole(data('buoys'), 'ocean')?.waves ?? byRole(data('buoys'), 'bar')?.waves;
	const powerKj = waves ? 0.183 * waves.ft * waves.ft * waves.periodS * waves.periodS : null;
	const wind = data('wind');
	const windKt = nearest(wind?.times, wind?.kt, now);
	const sky = data('sky');
	const low = nearest(sky?.times, sky?.lowWest ?? sky?.low, now);
	const tempF = nearest(sky?.times, sky?.tempWestF ?? sky?.tempF, now);
	const uv = nearest(sky?.times, sky?.uv, now);
	const parts = {
		surf: powerKj !== null ? scale(powerKj, cfg.surf.fullKj, cfg.surf.zeroKj) : null,
		wind: windKt !== null ? scale(windKt, cfg.wind.fullKt, cfg.wind.zeroKt) : null,
		sky: low !== null ? scale(low, cfg.sky.fullLow, cfg.sky.zeroLow) : null,
		warmth: tempF !== null ? scale(tempF, cfg.warmth.fullF, cfg.warmth.zeroF) : null,
	};
	const known = Object.entries(parts).filter(([, v]) => v !== null);
	if (!known.length) return null;
	const weight = known.reduce((t, [k]) => t + cfg.weights[k], 0);
	let score = Math.round((known.reduce((t, [k, v]) => t + cfg.weights[k] * v, 0) / weight) * 10);
	const hazard = beachAlert(data('alerts'), now);
	if (hazard && inEffect(hazard, now)) score = Math.min(score, cfg.hazardCap);
	const sea = byRole(data('buoys'), 'ocean')?.waterF ?? byRole(data('buoys'), 'bar')?.waterF;
	return {
		score, word: cfg.words.find(([max]) => score <= max)[1], waves, powerKj, windKt, windDir: nearest(wind?.times, wind?.dirDeg, now), low, tempF, uv, hazard, waterF: sea?.value ?? null,
	};
};
// the score's colour says good or bad, not its size (Oct 27, Jared): 7+ green, 4-6 yellow, 3 or less red
const scoreLevel = (score) => {
	if (score >= 7) return 'lv-good';
	return score >= 4 ? 'lv-fair' : 'lv-poor';
};
const meter = (score, kind = '') => `<div class="meter ${kind}">${Array.from({ length: 10 }, (_, i) => `<i class="${i < score ? 'on' : ''}"></i>`).join('')}</div>`;

class BayTides extends FeatureDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, 'Bay &amp; Tides');
	}

	// eslint-disable-next-line class-methods-use-this
	hasData() {
		const tides = data('tides');
		return !!tides?.events?.some((e) => new Date(e.t) > new Date());
	}

	// eslint-disable-next-line class-methods-use-this
	buildPages(now) {
		const tides = data('tides');
		const level = data('waterLevel');
		const buoys = data('buoys');
		const currents = data('currents');
		const alerts = data('alerts') ?? [];
		const bay = data('bayForecast');

		const next = tides.events.filter((e) => new Date(e.t) > now).slice(0, 4);
		const tideRows = next.map((e) => [
			e.type === 'H' ? 'High' : 'Low',
			`${isToday(e.t, now) ? '' : `${weekday(e.t)} `}${time(e.t)} &nbsp;${feet(e.ft)}`,
		]);
		// the observation is only "now" while it's recent
		const levelNow = level && now - new Date(level.t) < 2 * HOUR ? `Now ${feet(level.ft)}, ${tideTrend(tides, now)}` : '';

		// the next current that isn't slack, the slack after it, and the turn after that
		const coming = currents?.events.filter((e) => new Date(e.t) > now) ?? [];
		const firstMax = coming.findIndex((e) => e.type !== 'slack');
		const max = coming[firstMax];
		const slack = coming.find((e, i) => i > firstMax && e.type === 'slack') ?? coming.find((e) => e.type === 'slack');
		const after = coming.find((e) => e.type !== 'slack' && e !== max && new Date(e.t) > new Date(slack?.t ?? 0));
		const currentRows = [
			max && [`Max ${max.type}`, `${time(max.t)} ${max.kt.toFixed(1)} kt`],
			slack && ['Slack', time(slack.t)],
			after && [`Then ${after.type}`, `${time(after.t)} ${after.kt.toFixed(1)} kt`],
		];

		const pages = [{
			seconds: 12,
			title: ['Bay &', 'Tides'],
			html: `<div class="cols">
				<div>
					<div class="hdr">Tides &middot; ${escapeHtml(tides.station.name)}</div>
					${kv(tideRows)}
					<div class="small gap">${levelNow}</div>
				</div>
				<div>
					<div class="hdr">Today</div>
					${tideChart(tides, now)}
				</div>
			</div>
			<div class="asof">NOAA tide predictions</div>`,
		}];

		const bar = byRole(buoys, 'bar');
		const ocean = byRole(buoys, 'ocean');
		const waves = bar?.waves ?? ocean?.waves;
		const wind = ocean?.wind ?? byRole(buoys, 'south')?.wind;
		const waterF = bar?.waterF ?? ocean?.waterF;
		const oceanRows = [
			waves && ['Swell', `${feet(waves.ft)} @ ${Math.round(waves.periodS)} s ${compass(waves.dirDeg)}`],
			waterF && ['Water', `${Math.round(waterF.value)}&deg;`],
			wind && ['Wind', `${compass(wind.dirDeg)} ${Math.round(wind.kt)} kt`],
		];

		// the bay's own quiet tags (coastal flood, small craft), then any other alert for the bay zone, else the forecast
		const advisory = alerts.find((a) => tierOf(a.event) === 'bay' && (!a.ends || new Date(a.ends) > now))
			?? alerts.find((a) => a.zones.includes(water.bayZone.id) && tierOf(a.event) === 'crawl');
		const forecast = bay?.periods?.[0];
		const bayLine = advisory
			? `<div class="${tierOf(advisory.event) === 'bay' ? 'tag-line' : 'alert-line'}">${escapeHtml(advisory.event)}${advisory.ends ? ` until ${isToday(advisory.ends, now) ? '' : `${weekday(advisory.ends)} `}${time(advisory.ends)}` : ''}</div>`
			: '';
		const forecastText = forecast?.text ? `<div class="text long">${forecast.name ? `<span class="dim">${escapeHtml(forecast.name)}:</span> ` : ''}${escapeHtml(forecast.text)}</div>` : '';

		const buoyTime = waves?.at ?? wind?.at;
		if (oceanRows.some(Boolean) || currentRows.some(Boolean) || bayLine || forecastText) {
			pages.push({
				seconds: 12,
				title: ['Bay &', 'Ocean'],
				html: `<div class="cols">
					<div>
						<div class="hdr">Ocean &middot; ${escapeHtml((bar?.waves ? bar : ocean)?.name ?? 'buoys')}</div>
						${kv(oceanRows)}
						${currentRows.some(Boolean) ? `<div class="hdr gap">Golden Gate current</div>${kv(currentRows)}` : ''}
					</div>
					<div>
						<div class="hdr">Bay waters</div>
						${bayLine}${forecastText}
					</div>
				</div>
				<div class="asof">${buoyTime ? `Buoys ${asOf(buoyTime, now)}` : ''}</div>`,
			});
		}
		const beach = beachScore(now);
		if (beach) {
			const rows = [
				beach.waves && ['Surf', `${Math.round(beach.waves.ft)} ft @ ${Math.round(beach.waves.periodS)} s`],
				beach.windKt !== null && ['Wind', `${compass(beach.windDir)} ${Math.round(beach.windKt)} kt`],
				beach.tempF !== null && ['Air', `${Math.round(beach.tempF)}&deg;`],
				beach.low !== null && ['Low cloud', `${Math.round(beach.low)}%`],
				beach.waterF !== null && ['Water', `${Math.round(beach.waterF)}&deg;`],
				beach.uv !== null && beach.uv >= 1 && ['UV', `${Math.round(beach.uv)}`],
			];
			pages.push({
				seconds: 12,
				title: ['Beach', 'Day'],
				html: `<div class="cols crab">
					<div>
						<div class="hdr">${escapeHtml(water.beach.name)} &middot; now</div>
						${kv(rows)}
					</div>
					<div class="crab-score">
						<div class="hdr">Beach Day score</div>
						<div class="crab-big"><b class="${scoreLevel(beach.score)}">${beach.score}</b>/10</div>
						${meter(beach.score, 'beach')}
						<div class="small gap">${escapeHtml(beach.word)}</div>
						${beach.hazard ? `<div class="alert-line gap">${escapeHtml(dangers(beach.hazard))}</div><div class="small">${escapeHtml(alertWhen(beach.hazard, now))}</div>` : ''}
					</div>
				</div>
				<div class="asof">SF buoy &middot; Open-Meteo &middot; NWS surf zone</div>`,
			});
		}
		return pages;
	}
}

const display = new BayTides(14, 'bay-tides');
display.makeLoopScreen();
registerDisplay(display);
